import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { canAccessPage, PageKey } from "@/lib/rbac";
import AccessDenied from "@/pages/AccessDenied";

interface RequireRoleProps {
  children: React.ReactNode;
  pageKey: PageKey;
}

/**
 * Route guard component that checks if user has permission to access a page
 */
export function RequireRole({ children, pageKey }: RequireRoleProps) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // Not logged in: redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has required role
  // Pas de cast : `staffRole` est typé `StaffRole` à la source (AuthContext), c'est ce
  // qui garantit qu'un rôle ajouté au produit et oublié ici casse la compilation.
  const role = user?.staffRole ?? null;
  const hasAccess = canAccessPage(role, pageKey);

  if (!hasAccess) {
    return <AccessDenied />;
  }

  return <>{children}</>;
}

